/**
 * Ranked matches.
 *
 * THE SCORE IS NEVER SHOWN ALONE. Every incumbent that shipped a single opaque
 * percentage has walked it back, because a number people cannot interrogate
 * trains them to game it: they keyword-stuff, the metric goes up, and their
 * real outcome goes down. So the components sit beside the number, with the
 * evidence that produced them -- including the terms in the posting that are
 * NOT in their resume, which is the half that tells them something true.
 *
 * Compensation is shown only when the employer stated it. A modelled range
 * rendered like a fact can never be told apart from one again.
 *
 * Loading, empty and failed are three different states with three different
 * messages. This codebase has now been bitten five times by a screen that
 * showed "Loading…" forever because a failure had nowhere to go.
 */

import { useCallback, useEffect, useState } from "react";
import { FillHint } from "./FillHint";
import { Send } from "./Send";
import { SendTag, type SendRoute } from "./SendTag";
import type { ExtensionStatus } from "./extension";
import { api, ApiError } from "./auth";
import { PostingReader } from "./Posting";
import { ConfirmRemove } from "./fields";

/** Auto at a glance, from /api/me — the same shape the dashboard reads. */
interface AutoSummary {
  mode: "auto" | "review";
  sentToday: number;
  needsYou: number;
  browsers: number;
}

interface Component {
  label: string;
  value: number;
  evidence?: string[];
}

interface ScorerResult {
  scorer: "keyword" | "embedding" | "ontology";
  score: number;
  rationale: string;
  components: Component[];
}

type Stage = "saved" | "applied" | "screening" | "interviewing" | "offer" | "rejected" | "closed";

const STAGE_LABEL: Record<Stage, string> = {
  saved: "Saved", applied: "Applied", screening: "Screening", interviewing: "Interviewing",
  offer: "Offer", rejected: "Rejected", closed: "Closed",
};

/** What the seeker has already done about this role, if anything. */
interface Tracking {
  stage: Stage;
  appliedAt: string | null;
}

interface Match {
  jobId: string;
  tracking: Tracking | null;
  title: string;
  /** When ingestion first saw this posting. */
  firstSeenAt: string | null;
  /** First seen after the seeker's previous visit. */
  isNew: boolean;
  employerId: string;
  employer: string;
  locationMetro: string | null;
  place: string | null;
  isRemote: boolean;
  postedAt: string | null;
  applyUrl: string;
  /** How an application here goes out (from the server's ledger): autopilot / copilot / manual. */
  route: SendRoute;
  comp: { minCents: number | null; maxCents: number | null } | null;
  score: number;
  scorer: string;
  rationale: string;
  components: Component[];
  divergence: "agree" | "hidden_match" | "title_collision";
  /** Every scorer that ran. The headline number is one of these, never a blend. */
  scores: ScorerResult[];
}

interface Binding {
  key: string;
  label: string;
  count: number;
}

interface MatchesResponse {
  matches: Match[];
  /** How the whole match set (before this view's narrowing) splits across the three routes. */
  routeCounts?: { autopilot: number; copilot: number; manual: number };
  /** The route this response was filtered to, if any. */
  route?: SendRoute | null;
  empty: "not-empty" | "no-corpus" | "filters-too-narrow" | "no-profile" | "no-preferences";
  message?: string;
  scanned?: number;
  corpusSize?: number;
  /** The previous visit. Null on the first. */
  since?: string | null;
  /** Roles first seen since then, across everything that met the filters. */
  newCount?: number;
  /** Which single filter is doing the excluding, most permissive first. */
  binding?: Binding[];
  /** On the empty path: how many roles each target title matches on its own. */
  titleBreakdown?: Array<{ title: string; count: number }>;
  /**
   * On the empty path: real titles the listings use, close to the seeker's,
   * that the exact-title filter is hiding. One tap adds one to their search.
   */
  titleSuggestions?: Array<{ title: string; count: number }>;
  filters?: {
    query?: string | null;
    titles: string[];
    metros: string[];
    allowRemote: boolean;
    allowOnsite: boolean;
    compFloorCents: number | null;
    excluded: string[];
    hiddenEmployers?: number;
    hiddenRoles?: number;
    /** Whether jobs the seeker applies to themselves (the manual type) are shown. */
    includeManual?: boolean;
    /** How many matches are hidden right now because manual jobs are off. */
    manualHidden?: number;
  };
}

interface HiddenResponse {
  roles: Array<{ jobId: string; title: string; employer: string; hiddenAt: string; closed: boolean }>;
  employers: Array<{ id: string; name: string }>;
}

/** "yesterday at 8:52 PM", "Tuesday", "3 Sept" -- when the previous visit was. */
function sinceLabel(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "your last visit";
  const days = Math.floor((Date.now() - then.getTime()) / 86_400_000);
  const time = then.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  if (days === 0) return `${time} today`;
  if (days === 1) return `yesterday at ${time}`;
  if (days < 7) return then.toLocaleDateString(undefined, { weekday: "long" });
  return then.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function money(cents: number | null): string | null {
  if (cents == null) return null;
  return `$${Math.round(cents / 100_000).toLocaleString()}k`;
}

function payLabel(comp: Match["comp"]): string {
  if (!comp) return "No stated pay";
  const lo = money(comp.minCents);
  const hi = money(comp.maxCents);
  if (lo && hi && lo !== hi) return `${lo}–${hi}`;
  return lo ?? hi ?? "No stated pay";
}

function age(postedAt: string | null): string | null {
  if (!postedAt) return null;
  const days = Math.floor((Date.now() - Date.parse(postedAt)) / 86_400_000);
  if (!Number.isFinite(days) || days < 0) return null;
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "a month ago" : `${months} months ago`;
}

/**
 * A role fresh enough to be worth applying to early -- applicants in the
 * first day or two are more often reviewed first. From the employer's own
 * posted date only: when a board does not publish it, JobCork does not
 * guess (first-seen would misread an old role on a newly-added board).
 */
function earlyApplicant(postedAt: string | null): string | null {
  if (!postedAt) return null;
  const days = Math.floor((Date.now() - Date.parse(postedAt)) / 86_400_000);
  if (!Number.isFinite(days) || days < 0) return null;
  if (days === 0) return "Posted today — apply early";
  if (days <= 2) return "Fresh — apply early";
  return null;
}

/**
 * Flip whether matches include jobs the seeker applies to themselves (the
 * manual type). The whole active-track preference set round-trips -- the PUT
 * validates the lot -- so send back exactly what is there with only this
 * changed, the same way Auto's toggle does. The caller reloads on success.
 */
async function setIncludeManualPref(next: boolean): Promise<void> {
  const { preferences } = await api<{ preferences: Record<string, unknown> }>("/api/preferences");
  await api("/api/preferences", { method: "PUT", body: JSON.stringify({ ...preferences, includeManual: next }) });
}

export default function Matches({
  onBack,
  onOpenApplications,
  onOpenPreferences,
  onTailor,
  ext = null,
  auto = null,
  onAutoChanged,
  initialRoute = null,
}: {
  onBack: () => void;
  onOpenApplications: () => void;
  /** Open Auto's rules (the Preferences screen). */
  onOpenPreferences?: () => void;
  onTailor: (jobId: string) => void;
  /** The Chrome extension on this page, for saying where the fill happens beside each posting. */
  ext?: ExtensionStatus | null;
  /** Auto's state for this track, from /api/me. Null while it loads. */
  auto?: AutoSummary | null;
  /** Auto was turned on/off here; the shell re-reads /api/me so the rest stays in step. */
  onAutoChanged?: () => void;
  /** A send route to filter to on arrival, from the dashboard breakdown. Null = all. */
  initialRoute?: SendRoute | null;
}) {
  // Auto on/off lives on the server (search_preferences.apply_mode); this is the
  // local mirror so the banner and the per-card tags flip the instant it is
  // toggled, then the shell refresh confirms it.
  const [mode, setMode] = useState<"auto" | "review">(auto?.mode ?? "review");
  useEffect(() => { setMode(auto?.mode ?? "review"); }, [auto?.mode]);
  const [data, setData] = useState<MatchesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  // The list is compact (17 Sept): one row per match, and the row the person
  // clicks opens in place to the rationale, the breakdown, the posting and
  // the actions. One open at a time, so the page stays a list to scan.
  const [expanded, setExpanded] = useState<string | null>(null);
  const toggleRow = (jobId: string) => setExpanded((cur) => (cur === jobId ? null : jobId));
  /** Roles hidden during this visit, kept in place so Undo is one click. */
  const [hiddenNow, setHiddenNow] = useState<Set<string>>(new Set());
  /** The company just hidden, until the notice is dismissed or undone. */
  const [employerNotice, setEmployerNotice] = useState<{ id: string; name: string } | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  /** The search box: what is typed, and what the list currently reflects. */
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  /** Show only what is new since the previous visit. */
  const [onlyNew, setOnlyNew] = useState(false);
  /** The send-route filter — from the dashboard breakdown, or the chips here. Null = all routes. */
  const [route, setRoute] = useState<SendRoute | null>(initialRoute);
  useEffect(() => { setRoute(initialRoute); }, [initialRoute]);

  /** One card changed its tracking; the rest of the ranking is untouched. */
  const setTracking = (jobId: string, tracking: Tracking | null) =>
    setData((d) => d && {
      ...d,
      matches: d.matches.map((m) => (m.jobId === jobId ? { ...m, tracking } : m)),
    });

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    // A reload reflects the server's view, in which anything hidden during
    // this visit is already gone and already counted.
    setHiddenNow(new Set());
    const q = query.trim();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    // A route filter asks the server for that one route (bypassing the manual
    // hide) and shows more of it, since it is a deliberate drill-in.
    if (route) { params.set("route", route); params.set("limit", "100"); }
    const qs = params.toString();
    api<MatchesResponse>(qs ? `/api/matches?${qs}` : "/api/matches")
      .then(setData)
      .catch((err) =>
        setError(err instanceof ApiError ? `${err.message} (HTTP ${err.status})` : String(err)))
      .finally(() => setLoading(false));
  }, [query, route]);

  useEffect(load, [load]);

  const search = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery(draft.trim());
  };
  const clearSearch = () => {
    setDraft("");
    setQuery("");
  };

  const searchBox = (
    <form className="search" onSubmit={search} role="search">
      <input
        className="input"
        type="search"
        value={draft}
        placeholder="Search within these — CISSP, FedRAMP, Nashville, “zero trust”…"
        aria-label="Search within your matches"
        onChange={(e) => setDraft(e.target.value)}
      />
      <button className="btn ghost small" type="submit" disabled={loading}>Search</button>
      {query && (
        <button className="step-link small" type="button" onClick={clearSearch}>Clear</button>
      )}
    </form>
  );

  if (loading) {
    return (
      <section>
        {searchBox}
        <p className="muted">
          {query ? `Searching your matches for “${query}”…` : "Ranking the listings against your profile…"}
        </p>
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <h1>Matches</h1>
        <p className="err">{error}</p>
        <div className="row">
          <button className="btn" onClick={load}>Try again</button>
          <button className="btn ghost" onClick={onBack}>Back</button>
        </div>
      </section>
    );
  }

  if (!data) return null;

  const hiddenCount = (data.filters?.hiddenRoles ?? 0) + (data.filters?.hiddenEmployers ?? 0);
  const visible = data.matches.filter((m) => !hiddenNow.has(m.jobId)).length;
  const newHere = data.matches.filter((m) => m.isNew && !hiddenNow.has(m.jobId)).length;
  const shown = data.matches.filter((m) => !onlyNew || m.isNew || hiddenNow.has(m.jobId));

  return (
    <section>
      <header className="bar">
        <div>
          <h1 className="tight">Matches</h1>
          {data.scanned != null && (
            <p className="muted small">
              {visible} of {data.scanned - hiddenNow.size} roles that meet your filters
              {hiddenCount + hiddenNow.size > 0 && (
                <>
                  {" · "}
                  <button className="step-link small" onClick={() => setReviewOpen((v) => !v)}>
                    {reviewOpen ? "Hide the list" : `${hiddenCount + hiddenNow.size} hidden`}
                  </button>
                </>
              )}
            </p>
          )}
        </div>
        <button className="btn ghost small" onClick={onBack}>Back</button>
      </header>

      <AutoBanner
        mode={mode}
        auto={auto}
        onChanged={(next) => { setMode(next); onAutoChanged?.(); }}
        onOpenPreferences={onOpenPreferences}
      />

      {searchBox}

      {data.routeCounts && (data.routeCounts.autopilot + data.routeCounts.copilot + data.routeCounts.manual) > 0 && (
        <RouteTabs counts={data.routeCounts} active={route} onPick={setRoute} />
      )}

      {data.since != null && (
        <p className="since muted small">
          {data.newCount && data.newCount > 0 ? (
            <>
              <strong className="ink">{data.newCount} new</strong> since {sinceLabel(data.since)}
              {newHere < (data.newCount ?? 0) && newHere > 0 ? ` (${newHere} on this page)` : ""}
              {" · "}
              <button className="step-link small" onClick={() => setOnlyNew((v) => !v)}>
                {onlyNew ? "Show everything" : "Only new"}
              </button>
            </>
          ) : (
            <>Nothing new since {sinceLabel(data.since)}.</>
          )}
        </p>
      )}

      {data.filters && <FilterSummary filters={data.filters} />}

      {data.filters && !route && (
        <ManualFilter
          includeManual={data.filters.includeManual ?? true}
          manualHidden={data.filters.manualHidden ?? 0}
          onChanged={load}
        />
      )}

      {employerNotice && (
        <p className="notice">
          <strong>{employerNotice.name}</strong> is hidden — every role they post is out of your matches.{" "}
          <UndoEmployer employer={employerNotice} onDone={() => { setEmployerNotice(null); load(); }} />
          {" · "}
          <button className="step-link" onClick={() => setEmployerNotice(null)}>Dismiss</button>
        </p>
      )}

      {reviewOpen && <HiddenReview onChanged={load} onClose={() => setReviewOpen(false)} />}

      {data.matches.length === 0 && (route ? <RouteEmpty route={route} onClear={() => setRoute(null)} /> : <Empty data={data} onReload={load} />)}

      {onlyNew && shown.length === 0 && data.matches.length > 0 && (
        <p className="notice">None of the roles on this page are new since your last visit.</p>
      )}

      <ol className="matches">
        {shown.map((m) => hiddenNow.has(m.jobId) ? (
          <li key={m.jobId} className="match hidden-row">
            <span className="muted">Hidden: {m.title} · {m.employer}</span>
            <UnhideRole
              jobId={m.jobId}
              onDone={() => setHiddenNow((prev) => { const n = new Set(prev); n.delete(m.jobId); return n; })}
            />
          </li>
        ) : (
          <li key={m.jobId} className={`match app-row${expanded === m.jobId ? " open" : ""}`}>
            <div
              className="app-summary"
              role="button"
              tabIndex={0}
              aria-expanded={expanded === m.jobId}
              onClick={() => toggleRow(m.jobId)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleRow(m.jobId); } }}
            >
              <div className="app-main">
                <h3 className="tight">
                  {m.isNew && <span className="new" title="First seen since your last visit">New</span>}
                  {m.title}
                </h3>
                <p className="muted small nomargin">
                  {[m.employer, m.isRemote ? "Remote" : m.place, m.comp ? payLabel(m.comp) : null, age(m.postedAt) ? `posted ${age(m.postedAt)}` : null].filter(Boolean).join(" · ")}
                </p>
                <p className="tags" onClick={(e) => e.stopPropagation()}>
                  <SendTag applyUrl={m.applyUrl} route={m.route} mode={mode} />
                  {m.divergence === "hidden_match" && <span className="badge hidden-match app-chip" title="The work fits better than the words do">Hidden match</span>}
                  {m.divergence === "title_collision" && <span className="badge collision app-chip" title="Words match, work may not">Read carefully</span>}
                  {m.tracking && <span className="badge muted-badge app-chip">{m.tracking.stage === "saved" ? "Saved" : m.tracking.stage.charAt(0).toUpperCase() + m.tracking.stage.slice(1)}</span>}
                  {earlyApplicant(m.postedAt) && <span className="early" title="Early applicants are more often reviewed first">⚡ {earlyApplicant(m.postedAt)}</span>}
                </p>
              </div>
              <div className="match-score">
                <span className="fig">{Math.round(m.score * 100)}</span>
                <span className="cap">fit</span>
              </div>
              <span className="app-chev" aria-hidden="true">▾</span>
            </div>

            {expanded === m.jobId && (
            <div className="app-detail">
            {m.divergence === "hidden_match" && (
              <p className="badge hidden-match">
                Hidden match — the work fits better than the words do
              </p>
            )}
            {m.divergence === "title_collision" && (
              <p className="badge collision">
                Words match, work may not — read this one carefully
              </p>
            )}

            <p className="rationale">{m.rationale}</p>
            {m.scores.length > 1 && (
              <p className="muted small scorers">
                {m.scores.map((s) => (
                  <span key={s.scorer}>
                    {s.scorer === "embedding" ? "Meaning" : s.scorer === "keyword" ? "Words" : s.scorer}{" "}
                    <strong>{Math.round(s.score * 100)}</strong>
                  </span>
                ))}
              </p>
            )}

            <p className={m.comp ? "pay" : "pay muted"}>
              {payLabel(m.comp)}
              {!m.comp && (
                <span className="small"> — this employer did not publish a range</span>
              )}
            </p>

            <button
              className="step-link"
              aria-expanded={open === m.jobId}
              onClick={() => setOpen(open === m.jobId ? null : m.jobId)}
            >
              {open === m.jobId ? "Hide the breakdown" : "Why this scored what it did"}
            </button>

            {open === m.jobId && (
              <div className="breakdown">
                {(m.scores.length > 0 ? m.scores : [{ scorer: m.scorer, score: m.score, rationale: m.rationale, components: m.components } as ScorerResult]).map((s) => (
                  <section key={s.scorer}>
                    <h4 className="tight">
                      {s.scorer === "embedding"
                        ? "By meaning — your resume compared to the posting as a whole"
                        : s.scorer === "keyword"
                        ? "By words — which of your terms appear"
                        : s.scorer}
                      <span className="muted"> · {Math.round(s.score * 100)}</span>
                    </h4>
                    <ul className="components">
                      {s.components.map((c, i) => (
                        <li key={i}>
                          <div className="component-row">
                            <span>{c.label}</span>
                            {c.value > 0 && <span className="muted small">{Math.round(c.value * 100)}</span>}
                          </div>
                          {c.evidence && c.evidence.length > 0 && (
                            <p className="evidence">
                              {c.evidence.map((e) => (
                                <span className="tag" key={e}>{e}</span>
                              ))}
                            </p>
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
                {m.scores.length === 1 && m.scores[0].scorer === "keyword" && (
                  <p className="muted small">
                    Not yet compared by meaning — this posting's embedding is still being computed.
                  </p>
                )}
              </div>
            )}

            <PostingReader jobId={m.jobId} />

            <div className="row">
              <a className="btn" href={m.applyUrl} target="_blank" rel="noreferrer noopener">
                Open the posting
              </a>
              <button className="btn ghost" onClick={() => onTailor(m.jobId)}>Tailor my resume</button>
              {!(m.tracking && m.tracking.stage !== "saved") && <Send jobId={m.jobId} applyUrl={m.applyUrl} title={m.title} employer={m.employer} autoMode={mode} onOver={() => load()} />}
              <TrackingControls
                jobId={m.jobId}
                tracking={m.tracking}
                onChange={(t) => setTracking(m.jobId, t)}
                onOpenApplications={onOpenApplications}
              />
            </div>
            <FillHint applyUrl={m.applyUrl} ext={ext} autoMode={mode} />

            <FeedbackControls
              match={m}
              onRoleHidden={() => setHiddenNow((prev) => new Set(prev).add(m.jobId))}
              onEmployerHidden={(e) => { setEmployerNotice(e); load(); }}
            />
            </div>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * Filter the matches by how they apply — the same three the dashboard breakdown
 * shows, clickable here too. The counts are the whole match set's totals and
 * stay put whichever is active, so switching is one tap; "All" clears it.
 */
function RouteTabs({ counts, active, onPick }: {
  counts: { autopilot: number; copilot: number; manual: number };
  active: SendRoute | null;
  onPick: (r: SendRoute | null) => void;
}) {
  const total = counts.autopilot + counts.copilot + counts.manual;
  const tab = (r: SendRoute | null, label: string, n: number, cls: string) => (
    <button type="button" className={`rtab ${cls}${active === r ? " on" : ""}`} aria-pressed={active === r} onClick={() => onPick(r)}>
      {label} <span className="rtab-n">{n}</span>
    </button>
  );
  return (
    <div className="route-tabs" role="group" aria-label="Filter by how it applies">
      {tab(null, "All", total, "all")}
      {tab("autopilot", "Autopilot", counts.autopilot, "auto")}
      {tab("copilot", "Copilot", counts.copilot, "copilot")}
      {tab("manual", "Manual", counts.manual, "manual")}
    </div>
  );
}

/** A route filter that matched nothing — said plainly, with one tap back to all. */
function RouteEmpty({ route, onClear }: { route: SendRoute; onClear: () => void }) {
  const line = route === "autopilot"
    ? "No Autopilot matches right now — no employer in your matches lets the cloud send cleanly yet."
    : route === "copilot"
      ? "No Copilot matches right now — nothing in your matches needs your own browser to send."
      : "No Manual matches right now — everything in your matches, JobCork can fill for you.";
  return (
    <div className="notice">
      <p>{line}</p>
      <button className="btn" type="button" onClick={onClear}>Show all matches</button>
    </div>
  );
}

/**
 * Auto-apply, on or off, said plainly at the top of the matches (E14/E46).
 *
 * Steven's ask: a prominent control that shows whether Auto is on, because a
 * feature this large should never be something you have to go hunting for in a
 * settings screen to check. On here means JobCork applies to the strong
 * matches by the track's rules; the toggle writes search_preferences.apply_mode
 * for the active track (the same track these matches are for).
 *
 * Turning it on can be refused -- a search with no positive signal is too broad
 * to auto-apply against -- and the server says so; that message lands inline
 * rather than as a dead button. Nothing here sends an application: it sets the
 * mode, and the runner (extension or cloud) does the sending under its own gate.
 */
function AutoBanner({
  mode,
  auto,
  onChanged,
  onOpenPreferences,
}: {
  mode: "auto" | "review";
  auto: AutoSummary | null;
  onChanged: (mode: "auto" | "review") => void;
  onOpenPreferences?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const on = mode === "auto";

  const toggle = async () => {
    const next = on ? "review" : "auto";
    setBusy(true);
    setErr(null);
    try {
      // The whole active-track preference set round-trips: the PUT validates the
      // lot (and refuses too-broad Auto), so send back exactly what is there with
      // only the mode changed.
      const { preferences } = await api<{ preferences: Record<string, unknown> }>("/api/preferences");
      await api("/api/preferences", { method: "PUT", body: JSON.stringify({ ...preferences, applyMode: next }) });
      onChanged(next);
    } catch (e) {
      setErr(e instanceof ApiError ? e.messages.join(" ") : String(e));
    } finally {
      setBusy(false);
    }
  };

  const status = on
    ? [
        auto && auto.sentToday > 0 ? `${auto.sentToday} sent today` : "nothing sent yet today",
        auto && auto.needsYou > 0 ? `${auto.needsYou} ${auto.needsYou === 1 ? "needs" : "need"} you` : null,
        auto && auto.browsers === 0 ? "no browser connected — it runs from the cloud if you switched that on" : null,
      ].filter(Boolean).join(" · ")
    : "JobCork finds your strong matches, fills the form and presses Submit by your rules. You approve the rules once, not each send.";

  return (
    <div className={on ? "auto-banner on" : "auto-banner off"}>
      <div className="auto-banner-main">
        <span className="auto-dot" aria-hidden="true" />
        <div className="auto-banner-text">
          <p className="auto-title">Auto-apply is <strong>{on ? "ON" : "OFF"}</strong></p>
          <p className="muted small auto-sub">
            {status}
            {on && onOpenPreferences && (
              <> {" · "}<button className="step-link small" type="button" onClick={onOpenPreferences}>Auto's rules</button></>
            )}
          </p>
        </div>
      </div>
      <div className="auto-banner-actions">
        <button
          className={on ? "btn ghost" : "btn"}
          type="button"
          disabled={busy}
          aria-pressed={on}
          onClick={() => void toggle()}
        >
          {busy ? "Saving…" : on ? "Turn Auto off" : "Turn Auto on"}
        </button>
      </div>
      {err && <p className="err small auto-err">{err}</p>}
    </div>
  );
}

/**
 * The show/hide control for the manual apply type (E49), at the top of the
 * matches. Steven's realisation: gather the jobs JobCork cannot auto-fill and
 * show them too, since a seeker gets far more to apply to that way -- they just
 * apply by hand and mark it applied. On by default; a seeker who wants only
 * push-button matches turns it off. It writes a per-track preference (so the
 * choice sticks) and reloads, the same round-trip as Auto's toggle.
 */
function ManualFilter({
  includeManual,
  manualHidden,
  onChanged,
}: {
  includeManual: boolean;
  manualHidden: number;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = async () => {
    setBusy(true);
    setErr(null);
    try {
      await setIncludeManualPref(!includeManual);
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.messages.join(" ") : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <p className="notice manual-filter small">
      {includeManual ? (
        <>
          Showing every kind, including <strong>jobs you apply to yourself</strong> (Workday, employer sites) — you apply on their page and mark it applied here.{" "}
          <button className="step-link small" type="button" disabled={busy} onClick={toggle}>
            {busy ? "Saving…" : "Show only what JobCork can fill"}
          </button>
        </>
      ) : (
        <>
          Showing only jobs <strong>JobCork can fill</strong>
          {manualHidden > 0 ? ` · ${manualHidden} you'd apply to yourself ${manualHidden === 1 ? "is" : "are"} hidden` : ""}.{" "}
          <button className="step-link small" type="button" disabled={busy} onClick={toggle}>
            {busy ? "Saving…" : "Show those too"}
          </button>
        </>
      )}
      {err && <span className="err"> {err}</span>}
    </p>
  );
}

/** Turn manual jobs back on, from an empty result that hid them all. */
function ShowManualButton({ onChanged }: { onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const show = async () => {
    setBusy(true);
    setErr(null);
    try {
      await setIncludeManualPref(true);
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.messages.join(" ") : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button className="btn" type="button" disabled={busy} onClick={show}>
        {busy ? "Showing…" : "Show jobs I apply to myself"}
      </button>
      {err && <p className="err small">{err}</p>}
    </>
  );
}

/**
 * Two ways to say no, one line, below the actions. A "no" narrows the search
 * and does nothing else -- it is not a rating and it does not reorder the rest.
 *
 * Hiding a role keeps the card in place as a one-line stub with Undo. Hiding a
 * company reloads the list, because every one of its roles goes, and leaves a
 * notice with Undo at the top.
 */
function FeedbackControls({
  match,
  onRoleHidden,
  onEmployerHidden,
}: {
  match: Match;
  onRoleHidden: () => void;
  onEmployerHidden: (employer: { id: string; name: string }) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const hideRole = () => {
    setBusy(true);
    setErr(null);
    api<{ hidden: boolean }>(`/api/hidden/roles/${match.jobId}`, { method: "PUT", body: "{}" })
      .then(onRoleHidden)
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  const hideEmployer = () => {
    setBusy(true);
    setErr(null);
    api<{ hidden: boolean; employer: { id: string; name: string } }>(
      `/api/hidden/employers/${match.employerId}`, { method: "PUT", body: "{}" },
    )
      .then((r) => onEmployerHidden(r.employer))
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  return (
    <p className="feedback muted small">
      <button className="step-link small" disabled={busy} onClick={hideRole}>Not for me</button>
      {" · "}
      <button className="step-link small" disabled={busy} onClick={hideEmployer}>Hide {match.employer}</button>
      {err && <span className="err"> {err}</span>}
    </p>
  );
}

function UnhideRole({ jobId, onDone }: { jobId: string; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const undo = () => {
    setBusy(true);
    setErr(null);
    api<{ hidden: boolean }>(`/api/hidden/roles/${jobId}`, { method: "DELETE" })
      .then(onDone)
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setBusy(false));
  };
  return (
    <span>
      <button className="step-link" disabled={busy} onClick={undo}>Undo</button>
      {err && <span className="err small"> {err}</span>}
    </span>
  );
}

function UndoEmployer({ employer, onDone }: { employer: { id: string; name: string }; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const undo = () => {
    setBusy(true);
    setErr(null);
    api<{ hidden: boolean }>(`/api/hidden/employers/${employer.id}`, { method: "DELETE" })
      .then(onDone)
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setBusy(false));
  };
  return (
    <span>
      <button className="step-link" disabled={busy} onClick={undo}>Undo</button>
      {err && <span className="err small"> {err}</span>}
    </span>
  );
}

/**
 * Everything the seeker has hidden, so a decision made weeks ago can be
 * reversed without remembering it. Its own loading and failure states, like
 * every other fetch on this screen.
 */
function HiddenReview({ onChanged, onClose }: { onChanged: () => void; onClose: () => void }) {
  const [data, setData] = useState<HiddenResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    api<HiddenResponse>("/api/hidden")
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? `${e.message} (HTTP ${e.status})` : String(e)));
  }, []);
  useEffect(load, [load]);

  const unhide = (path: string, key: string) => {
    setBusy(key);
    setError(null);
    api(path, { method: "DELETE" })
      .then(() => { load(); onChanged(); })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setBusy(null));
  };

  return (
    <div className="notice review">
      <div className="bar slim">
        <strong>What you have hidden</strong>
        <button className="step-link" onClick={onClose}>Close</button>
      </div>
      {error && <p className="err small">{error}</p>}
      {!data && !error && <p className="muted small">Loading…</p>}
      {data && data.employers.length === 0 && data.roles.length === 0 && (
        <p className="muted small">Nothing yet. "Not for me" hides one role; "Hide company" hides every role they post.</p>
      )}
      {data && data.employers.length > 0 && (
        <>
          <p className="small"><strong>Companies</strong> — none of their roles are shown</p>
          <ul className="bindings">
            {data.employers.map((e) => (
              <li key={e.id}>
                {e.name || <span className="muted">(employer no longer in the listings)</span>}{" · "}
                <button className="step-link small" disabled={busy === e.id}
                  onClick={() => unhide(`/api/hidden/employers/${e.id}`, e.id)}>Unhide</button>
              </li>
            ))}
          </ul>
        </>
      )}
      {data && data.roles.length > 0 && (
        <>
          <p className="small"><strong>Roles</strong> marked not for you</p>
          <ul className="bindings">
            {data.roles.map((r) => (
              <li key={r.jobId}>
                {r.title}{r.employer ? ` · ${r.employer}` : ""}
                {r.closed && <span className="muted"> · since closed</span>}{" · "}
                <button className="step-link small" disabled={busy === r.jobId}
                  onClick={() => unhide(`/api/hidden/roles/${r.jobId}`, r.jobId)}>Unhide</button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Save / I applied, on the card, one click each. The record it writes is the
 * same row auto-apply will write later, so what the seeker does by hand and
 * what the service does for them end up in one pipeline.
 *
 * A failure shows on the card itself. A button that silently does nothing is
 * the swallowed-failure shape this codebase keeps paying for.
 */
function TrackingControls({
  jobId,
  tracking,
  onChange,
  onOpenApplications,
}: {
  jobId: string;
  tracking: Tracking | null;
  onChange: (t: Tracking | null) => void;
  onOpenApplications: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const put = (stage: Stage) => {
    setBusy(true);
    setErr(null);
    api<{ application: Tracking }>(`/api/applications/${jobId}`, {
      method: "PUT",
      body: JSON.stringify({ stage }),
    })
      .then((r) => onChange({ stage: r.application.stage, appliedAt: r.application.appliedAt }))
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  const remove = () => {
    setBusy(true);
    setErr(null);
    api<{ removed: boolean }>(`/api/applications/${jobId}`, { method: "DELETE" })
      .then(() => onChange(null))
      .catch((e) => setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  if (!tracking) {
    return (
      <span className="tracking">
        <button className="btn ghost" disabled={busy} onClick={() => put("saved")}>Save for later</button>
        <button className="btn ghost" disabled={busy} onClick={() => put("applied")}>I applied</button>
        {err && <span className="err small">{err}</span>}
      </span>
    );
  }

  if (tracking.stage === "saved") {
    return (
      <span className="tracking">
        <span className="tracked">Saved</span>
        <button className="btn ghost" disabled={busy} onClick={() => put("applied")}>I applied</button>
        <ConfirmRemove onRemove={remove} busy={busy} className="step-link" />
        {err && <span className="err small">{err}</span>}
      </span>
    );
  }

  const when = age(tracking.appliedAt);
  return (
    <span className="tracking">
      <span className="tracked">
        Applied{when ? ` ${when}` : ""}
        {tracking.stage !== "applied" ? ` · ${STAGE_LABEL[tracking.stage]}` : ""}
      </span>
      <button className="step-link" onClick={onOpenApplications}>Update in Applications</button>
      {err && <span className="err small">{err}</span>}
    </span>
  );
}

function FilterSummary({ filters }: { filters: NonNullable<MatchesResponse["filters"]> }) {
  const parts: string[] = [];
  if (filters.query) parts.push(`mentioning “${filters.query}”`);
  if (filters.titles.length > 0) parts.push(filters.titles.join(", "));
  if (filters.metros.length > 0) parts.push(filters.metros.join(", "));
  if (filters.allowRemote && !filters.allowOnsite) parts.push("remote only");
  if (!filters.allowRemote && filters.allowOnsite) parts.push("on site or hybrid only");
  if (filters.compFloorCents) parts.push(`at least ${money(filters.compFloorCents)}`);
  if (filters.excluded.length > 0) parts.push(`never ${filters.excluded.join(", ")}`);
  if ((filters.hiddenEmployers ?? 0) > 0) {
    parts.push(`${filters.hiddenEmployers} compan${filters.hiddenEmployers === 1 ? "y" : "ies"} hidden`);
  }

  return (
    <p className="notice">
      {parts.length === 0
        ? "You have not set any filters yet, so this is everything open."
        : `Obeying exactly: ${parts.join(" · ")}.`}
    </p>
  );
}

/**
 * An empty result is an answer, and the reason decides whose problem it is.
 * "No matches" for all three cases is how a working product looks broken.
 */
function Empty({ data, onReload }: { data: MatchesResponse; onReload: () => void }) {
  // The list is empty only because the seeker turned off the jobs they'd apply
  // to themselves, and there were some. Say that plainly and offer them back,
  // rather than blaming their titles or metros -- those are not what emptied it.
  if (data.filters && data.filters.includeManual === false && (data.filters.manualHidden ?? 0) > 0) {
    const n = data.filters.manualHidden ?? 0;
    return (
      <div className="notice">
        <p><strong>{n}</strong> role{n === 1 ? "" : "s"} match, but {n === 1 ? "it's one" : "they're ones"} you'd apply to yourself — hidden right now.</p>
        <p className="muted small">
          JobCork can't fill these, but you can apply on the employer's page and mark each applied here to track it.
        </p>
        <ShowManualButton onChanged={onReload} />
      </div>
    );
  }
  if (data.empty === "no-profile" || data.empty === "no-preferences") {
    return (
      <div className="notice">
        <p>{data.message}</p>
      </div>
    );
  }
  if (data.empty === "no-corpus") {
    return (
      <div className="notice">
        <p>There are no open jobs in the listings yet.</p>
        <p className="muted small">
          That is on us, not your filters — we have not gathered any listings yet.
        </p>
      </div>
    );
  }
  const binding = data.binding ?? [];
  return (
    <div className="notice">
      <p>
        {data.filters?.query
          ? `Nothing open mentions “${data.filters.query}” within your filters.`
          : "Nothing open matches your filters right now."}
      </p>
      <p className="muted small">
        We searched {data.corpusSize?.toLocaleString() ?? "the"} open roles and did not
        relax anything to fill the page.
      </p>

      {binding.length > 0 ? (
        <>
          <p className="small">Here is what each constraint is costing you:</p>
          <ul className="bindings">
            {binding.map((b) => (
              <li key={b.key}>
                Dropping <strong>{b.label}</strong> alone would return{" "}
                {b.count.toLocaleString()} role{b.count === 1 ? "" : "s"}.
              </li>
            ))}
          </ul>
          {data.titleBreakdown && data.titleBreakdown.length > 0 && (
            <>
              <p className="small">Your target titles, one at a time, with everything else kept:</p>
              <ul className="bindings">
                {data.titleBreakdown.map((t) => (
                  <li key={t.title}>
                    <strong>{t.title}</strong> — {t.count.toLocaleString()} role{t.count === 1 ? "" : "s"}
                    {t.count === 0 && <span className="muted"> · no posting title contains every word of this</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="muted small">
            Change one of these in your preferences — you do not have to give up
            the rest.
          </p>
        </>
      ) : (
        <p className="muted small">
          Relaxing any single filter still returns nothing, so it is the
          combination rather than one setting.
        </p>
      )}

      {data.titleSuggestions && data.titleSuggestions.length > 0 && (
        <TitleSuggestions suggestions={data.titleSuggestions} onAdded={onReload} />
      )}
    </div>
  );
}

/**
 * Real titles the listings use, close to what the seeker typed, that the exact
 * filter is hiding -- "QA Manager", "Test Lead" for someone searching "Test
 * Manager". Each is one tap to add to their own titles; the count is how many
 * open roles carry it right now.
 *
 * This is the only place the search widens, and only because the person asked:
 * a tap adds a title they can see and remove. Retrieval itself stays exact, so
 * "obeyed exactly" holds -- the list obeys a longer set of titles, not a looser
 * rule. Adding writes the whole active-track preference set back (the PUT
 * validates the lot), then reloads, so the roles the new title unlocks appear
 * at once.
 */
function TitleSuggestions({
  suggestions,
  onAdded,
}: {
  suggestions: Array<{ title: string; count: number }>;
  onAdded: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);

  const add = async (title: string) => {
    setBusy(title);
    setErr(null);
    try {
      const { preferences } = await api<{ preferences: { titles?: string[] } & Record<string, unknown> }>(
        "/api/preferences",
      );
      const current = Array.isArray(preferences.titles) ? preferences.titles : [];
      const titles = current.some((t) => t.toLowerCase() === title.toLowerCase())
        ? current
        : [...current, title];
      await api("/api/preferences", { method: "PUT", body: JSON.stringify({ ...preferences, titles }) });
      setAdded((prev) => new Set(prev).add(title));
      onAdded();
    } catch (e) {
      setErr(e instanceof ApiError ? e.messages.join(" ") : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="suggest-titles">
      <p className="small">
        Titles the open listings actually use, close to yours. Add the ones you mean —
        your search still matches exactly the titles you list:
      </p>
      <ul className="suggest-chips">
        {suggestions.map((s) => (
          <li key={s.title}>
            <button
              type="button"
              className={added.has(s.title) ? "chip on" : "chip"}
              disabled={busy !== null || added.has(s.title)}
              onClick={() => add(s.title)}
              title={`${s.count.toLocaleString()} open role${s.count === 1 ? "" : "s"} use this title`}
            >
              <span aria-hidden="true">{added.has(s.title) ? "✓" : "+"}</span> {s.title}
              <span className="chip-n">{s.count.toLocaleString()}</span>
            </button>
          </li>
        ))}
      </ul>
      {err && <p className="err small">{err}</p>}
    </div>
  );
}
